(() => {
  const progressSettings = () => {
    const threshold = Number(document.querySelector('meta[name="afs-free-shipping-threshold"]')?.content);
    const currency = document.querySelector('meta[name="afs-free-shipping-currency"]')?.content?.trim().toUpperCase();
    return { threshold, currency };
  };
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency
  }).format(cents / 100);
  const cartHost = () => document.querySelector('.cart-drawer__summary, .cart-summary__inner, .cart-summary');
  const setupPriceNode = () => document.querySelector('[data-afs-total], [data-afs-build-price], [data-afs-three-pole-price]');
  const productHosts = () => Array.from(document.querySelectorAll('[data-afs-product-shipping-progress], [data-afs-header-shipping]'));
  const nestLinkedCartLines = () => {
    for (const body of document.querySelectorAll('.cart-items__table tbody')) {
      const rows = Array.from(body.querySelectorAll(':scope > .cart-items__table-row[data-afs-setup]'));
      const mounts = new Map(rows.filter((row) => row.dataset.afsRole === 'mount').map((row) => [row.dataset.afsSetup, row]));
      const children = new Map();
      for (const row of rows.filter((row) => row.dataset.afsRole !== 'mount' && mounts.has(row.dataset.afsSetup))) {
        const lines = children.get(row.dataset.afsSetup) || [];
        lines.push(row);
        children.set(row.dataset.afsSetup, lines);
      }
      const ordered = [];
      for (const row of rows) {
        if (row.dataset.afsRole !== 'mount' && mounts.has(row.dataset.afsSetup)) continue;
        ordered.push(row);
        if (row.dataset.afsRole === 'mount') ordered.push(...(children.get(row.dataset.afsSetup) || []));
      }
      ordered.forEach((row, index) => { row.style.order = String(index); });
      body.classList.toggle('afs-cart-grouped', ordered.length > 0);
    }
  };
  const formattedCents = (value) => {
    const match = String(value || '').replace(/\s/g, '').match(/[\d,.]+/);
    if (!match) return null;
    const amount = Number(match[0].replace(/,/g, ''));
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  };
  const setupTotal = () => {
    const price = setupPriceNode();
    const cents = formattedCents(price?.textContent);
    if (cents === null) return null;
    const quantity = Number(price.closest('.afs-cfg__footer')?.querySelector('.afs-qty-stepper__input')?.value) || 1;
    return cents * Math.max(1, quantity);
  };
  let cartPromise;
  const getCart = () => cartPromise || (cartPromise = fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin'
  }).then((response) => response.json()).finally(() => setTimeout(() => {
    cartPromise = null;
  }, 250)));
  const progressMarkup = (remaining, percent, currency, qualifiedText, remainingTemplate) => {
    const amount = '<strong>' + money(remaining, currency) + '</strong>';
    const template = remainingTemplate || 'Add [amount] more to unlock free shipping.';
    const message = remaining
      ? template.replace(/\[\s*amount\s*\]|\{\{\s*amount\s*\}\}/, amount)
      : '<strong>' + qualifiedText + '</strong>';
    return '<p>' + message + '</p><div class="afs-shipping-progress__track" aria-hidden="true"><span class="afs-shipping-progress__bar" style="width:' + percent + '%"></span></div>';
  };
  const clearHosts = (hosts) => hosts.forEach((host) => {
    host.hidden = true;
    host.replaceChildren();
  });
  const updateHost = (host, markup) => {
    if (host.innerHTML !== markup) host.innerHTML = markup;
    host.hidden = false;
  };
  /* In the drawer the progress box sits in a black band straight under the
     "Your Bag" header, not in the Order Summary. The summary still renders its
     own copy (hidden by afs-cart.css); it is moved into the band here, and the
     band is re-created if the drawer re-renders. Empty drawer: no box. */
  const drawerContent = () => {
    const content = document.querySelector('#cart-drawer .cart-drawer__content');
    return content?.querySelector('.cart-drawer__summary') ? content : null;
  };
  const drawerBandMissing = () => {
    const content = drawerContent();
    return Boolean(content && !content.querySelector(':scope > .afs-cart-drawer__shipping [data-afs-cart-shipping-progress]'));
  };
  let cartProgressOff = false;
  const renderCartProgress = (cart, threshold, configuredCurrency, cartCurrency) => {
    const content = drawerContent();
    const host = content || cartHost();
    if (!host) return;
    let band = null;
    if (content) {
      band = content.querySelector(':scope > .afs-cart-drawer__shipping');
      if (!band) {
        band = document.createElement('div');
        band.className = 'afs-cart-drawer__shipping';
        content.prepend(band);
      }
    }
    const nodes = Array.from(host.querySelectorAll('[data-afs-cart-shipping-progress], .afs-shipping-progress'));
    let node = (band && band.querySelector('[data-afs-cart-shipping-progress], .afs-shipping-progress')) || nodes[0];
    if (!Number.isFinite(threshold) || threshold <= 0 || !configuredCurrency || cartCurrency !== configuredCurrency) {
      cartProgressOff = true;
      nodes.forEach((item) => item.remove());
      band?.remove();
      return;
    }
    cartProgressOff = false;
    if (!node) {
      node = document.createElement('div');
      node.className = 'afs-shipping-progress';
      node.dataset.afsCartShippingProgress = '';
      host.prepend(node);
    }
    if (band) {
      nodes.forEach((item) => { if (item !== node) item.remove(); });
      if (node.parentElement !== band) band.append(node);
    }
    const remaining = Math.max(threshold - cart.total_price, 0);
    const percent = Math.min(100, Math.round(cart.total_price / threshold * 100));
    node.innerHTML = progressMarkup(remaining, percent, cartCurrency, 'You have unlocked free shipping.');
  };
  const renderProductProgress = (cart, threshold, configuredCurrency, cartCurrency) => {
    const hosts = productHosts();
    if (!hosts.length) return;
    if (!Number.isFinite(threshold) || threshold <= 0 || !configuredCurrency || cartCurrency !== configuredCurrency) {
      clearHosts(hosts);
      return;
    }
    const remaining = Math.max(threshold - cart.total_price, 0);
    const percent = Math.min(100, Math.round(cart.total_price / threshold * 100));
    const settings = document.querySelector('[data-afs-shipping-settings]');
    hosts.forEach((host) => {
      const markup = progressMarkup(
        remaining,
        percent,
        cartCurrency,
        'You have unlocked free shipping.',
        host.dataset.afsShippingRemainingTemplate || settings?.dataset.afsShippingRemainingTemplate || 'Add [amount] more to unlock free shipping.'
      );
      updateHost(host, markup);
    });
  };
  const render = async () => {
    if (!cartHost() && !productHosts().length) return;
    const cart = await getCart();
    const { threshold, currency: configuredCurrency } = progressSettings();
    const cartCurrency = String(cart.currency || configuredCurrency || '').toUpperCase();
    renderCartProgress(cart, threshold, configuredCurrency, cartCurrency);
    renderProductProgress(cart, threshold, configuredCurrency, cartCurrency);
  };
  let frame;
  const refresh = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = undefined;
      cartPromise = null;
      render().catch(() => {});
    });
  };
  let observedPrice;
  let priceObserver;
  let setupPriceRetry;
  let cartGroupingFrame;
  const scheduleCartGrouping = () => {
    if (cartGroupingFrame) return;
    cartGroupingFrame = window.requestAnimationFrame(() => {
      cartGroupingFrame = undefined;
      nestLinkedCartLines();
    });
  };
  const observeSetupPrice = () => {
    const price = setupPriceNode();
    if (price === observedPrice) return;
    priceObserver?.disconnect();
    observedPrice = price;
    if (price) {
      priceObserver = new MutationObserver(refresh);
      priceObserver.observe(price, { childList: true, characterData: true, subtree: true });
    }
    refresh();
  };
  const waitForSetupPrice = () => {
    window.clearTimeout(setupPriceRetry);
    let attempts = 0;
    const retry = () => {
      observeSetupPrice();
      if (setupTotal() !== null) return;
      attempts += 1;
      if (attempts < 24) setupPriceRetry = window.setTimeout(retry, 250);
    };
    retry();
  };
  const start = () => {
    observeSetupPrice();
    waitForSetupPrice();
    scheduleCartGrouping();
    new MutationObserver(() => {
      observeSetupPrice();
      scheduleCartGrouping();
      if (!cartProgressOff && drawerBandMissing()) refresh();
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  const refreshAfterCartUpdate = (event) => {
    cartPromise = null;
    refresh();
    event?.promise?.then(() => {
      cartPromise = null;
      refresh();
    }).catch(() => {});
  };
  document.addEventListener('shopify:cart:lines-update', refreshAfterCartUpdate);
  document.addEventListener('shopify:cart:discount-update', refreshAfterCartUpdate);
  document.addEventListener('cart:updated', refreshAfterCartUpdate);
  document.addEventListener('cart-lines:update', refreshAfterCartUpdate);
  document.addEventListener('input', (event) => {
    if (event.target.matches('.afs-qty-stepper__input')) refresh();
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('.afs-qty-stepper__input')) refresh();
  });
  document.addEventListener('shopify:section:load', waitForSetupPrice);
})();
